import React, { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import AddOutlined from '@mui/icons-material/AddOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { qdfAPI } from '../../../services/api';
import { SC_STATUS_OPTIONS } from '../QDFConstants';
import { AuthContext } from '../../../contexts/AuthContext';
import {
  INSPECTION_STATUS_OPTIONS,
  buildEmptyInspection,
  getChecklistForPpmType,
  summarizeInspection,
} from '../inspectionChecklists';
import { Modal, Button, Badge, Input, Textarea, Select, FieldSet, Alert } from '../../../components/ui';
import InspectionStatusToggle from './InspectionStatusToggle';

const blankSparePart = () => ({ deviceName: '', description: '', quantity: '', remarks: '' });
const hasSparePartValue = (part) =>
  Boolean(
    part?.deviceName?.trim() ||
      part?.description?.trim() ||
      String(part?.quantity || '').trim() ||
      part?.remarks?.trim()
  );

const buildStatusState = (entry) => entry?.scStatus || '';
const DEFAULT_REMARK = 'checked';

const mergeWithCatalog = (entry) => {
  const ppmType = entry?.ppmType;
  const catalog = getChecklistForPpmType(ppmType);
  const existing = entry?.inspection;

  if (!existing || !Array.isArray(existing.items) || existing.items.length === 0) {
    return buildEmptyInspection(ppmType);
  }

  // Keep saved status/remarks aligned with the canonical catalog ordering so
  // changes to the catalog don't desync historical inspections.
  const bySr = new Map(existing.items.map((it) => [it.sr, it]));
  const items = catalog.map((row) => {
    const saved = bySr.get(row.sr);
    return {
      sr: row.sr,
      description: row.description,
      status: saved?.status || '',
      remarks: saved?.remarks?.trim() ? saved.remarks : DEFAULT_REMARK,
    };
  });

  return {
    ppmType,
    items,
    spareParts: Array.isArray(existing.spareParts) ? existing.spareParts : [],
    comments: existing.comments || '',
    preparedBy: existing.preparedBy || '',
    completedByUser: existing.completedByUser || '',
    completedAt: existing.completedAt || null,
  };
};

const InspectionChecklistModal = ({ isOpen, onClose, entry, readOnly = false, onSaved }) => {
  const { username: authUsername } = useContext(AuthContext);
  const [inspection, setInspection] = useState(() => mergeWithCatalog(entry));
  const [scStatus, setScStatus] = useState(() => buildStatusState(entry));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setInspection(mergeWithCatalog(entry));
      setScStatus(buildStatusState(entry));
    }
  }, [isOpen, entry]);

  const summary = useMemo(() => summarizeInspection(inspection), [inspection]);

  if (!isOpen || !entry) return null;

  const ppmType = entry.ppmType;
  const catalogExists = getChecklistForPpmType(ppmType).length > 0;
  const spareParts = inspection.spareParts.length > 0 ? inspection.spareParts : [blankSparePart()];
  const progressPct = summary.total ? Math.round(((summary.total - summary.pending) / summary.total) * 100) : 0;

  const setItem = (idx, patch) => {
    setInspection((prev) => {
      const items = prev.items.slice();
      items[idx] = { ...items[idx], ...patch };
      return { ...prev, items };
    });
  };

  const setSparePart = (idx, patch) => {
    setInspection((prev) => {
      const next = (prev.spareParts.length > 0 ? prev.spareParts : [blankSparePart()]).slice();
      next[idx] = { ...blankSparePart(), ...next[idx], ...patch };
      return { ...prev, spareParts: next };
    });
  };

  const addSparePart = () =>
    setInspection((prev) => ({
      ...prev,
      spareParts: [...(prev.spareParts.length > 0 ? prev.spareParts : [blankSparePart()]), blankSparePart()],
    }));

  const removeSparePart = (idx) =>
    setInspection((prev) => ({
      ...prev,
      spareParts: prev.spareParts.filter((_, i) => i !== idx),
    }));

  const applyBulkStatus = (status) => {
    setInspection((prev) => ({
      ...prev,
      items: prev.items.map((item) => ({ ...item, status })),
    }));
    toast.success(`Applied ${status} to all ${inspection.items.length} items.`);
  };

  const handleSave = async ({ markComplete = false } = {}) => {
    if (!entry?._id) {
      toast.error('Cannot save: missing work order id.');
      return;
    }

    const preparedBy = (inspection.preparedBy || '').trim();
    const nextScStatus = markComplete ? 'Closed' : scStatus;
    const isClosedStatus = String(nextScStatus || '').toLowerCase() === 'closed';
    const completedByUser = markComplete
      ? String(authUsername || '').trim()
      : isClosedStatus
        ? inspection.completedByUser || ''
        : '';

    if (markComplete) {
      if (!summary.isComplete) {
        toast.error('All items must be marked Pass, Fail, or N/A before completing.');
        return;
      }
      if (!preparedBy) {
        toast.error('Please fill in "Prepared by" before completing.');
        return;
      }
    }

    const payload = {
      scStatus: nextScStatus,
      inspection: {
        ...inspection,
        preparedBy,
        completedByUser,
        spareParts: (inspection.spareParts || []).filter(hasSparePartValue),
        completedAt: markComplete
          ? new Date().toISOString()
          : isClosedStatus
            ? inspection.completedAt
            : null,
      },
    };

    try {
      setSaving(true);
      const res = await qdfAPI.updateReport(entry._id, payload);
      const updated = res?.data?.report || { ...entry, ...payload };
      toast.success(markComplete ? 'Inspection completed and saved.' : 'Inspection saved.');
      if (onSaved) onSaved(updated);
      onClose();
    } catch (err) {
      console.error('Failed to save inspection:', err);
      toast.error(err?.response?.data?.message || 'Failed to save inspection.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={entry.scNumber || 'Work order'}
      subtitle={[entry.location, entry.zone && `Zone ${entry.zone}`, entry.assetTag]
        .filter(Boolean)
        .join(' · ')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Close
          </Button>
          {!readOnly && (
            <>
              <Button variant="secondary" onClick={() => handleSave({ markComplete: false })} loading={saving}>
                Save progress
              </Button>
              <Button variant="success" onClick={() => handleSave({ markComplete: true })} loading={saving}>
                Complete &amp; save
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge variant="type" size="sm">PPM inspection</Badge>
        <Badge variant="neutral" size="sm">{ppmType || 'Unknown'}</Badge>
        {readOnly && <Badge variant="warning" size="sm">Read only</Badge>}
      </div>

      <div className="z-10 -mx-4 mb-5 border-b border-border bg-surface px-4 pb-3 pt-1 sm:-mx-6 sm:px-6">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="tabular font-medium text-foreground">{progressPct}% complete</span>
          <span className="text-muted-fg">
            ({summary.total - summary.pending}/{summary.total} items)
          </span>
          <div className="ml-auto flex flex-wrap gap-1.5">
            <Badge variant="success" size="sm">Pass {summary.pass}</Badge>
            <Badge variant="danger" size="sm">Fail {summary.fail}</Badge>
            <Badge variant="neutral" size="sm">N/A {summary.na}</Badge>
            {summary.pending > 0 && <Badge variant="warning" size="sm">Pending {summary.pending}</Badge>}
          </div>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-success transition-all duration-slow ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div className="space-y-8">
        {!catalogExists && (
          <Alert variant="warning">
            No inspection checklist is defined for PPM type “{ppmType || 'Unknown'}”.
          </Alert>
        )}

        <FieldSet legend="Work order status">
          <div className="max-w-sm">
            <Select
              label="Status"
              value={scStatus}
              disabled={readOnly}
              onChange={(e) => setScStatus(e.target.value)}
              hint="“Complete & save” marks this PM as Closed automatically."
            >
              <option value="">Select status</option>
              {SC_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </Select>
          </div>
        </FieldSet>

        {catalogExists && (
          <section>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-fg">
                Operation checklist
              </h4>
              {!readOnly && (
                // One tap per status beats a select plus an Apply button, and it
                // sits on the heading line instead of floating away from it.
                <div className="flex items-center gap-1">
                  <span className="mr-1 text-xs text-muted-fg">Set all to</span>
                  {/* Ghost, so a bulk action is never mistaken for the boxed
                      per-item toggles it sits above. */}
                  {INSPECTION_STATUS_OPTIONS.map((status) => (
                    <Button
                      key={status}
                      variant="ghost"
                      size="sm"
                      onClick={() => applyBulkStatus(status)}
                    >
                      {status}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* One card per item. Stacked at phone width; the status and remarks
                move onto the description's line from `md`, which roughly halves
                the height of a ten-item checklist on a laptop. */}
            <ul className="space-y-2">
              {inspection.items.map((item, idx) => (
                <li
                  key={item.sr}
                  className={`rounded-lg border p-3 transition-colors duration-fast ${
                    item.status === 'Fail'
                      ? 'border-danger/40 bg-danger-soft/40'
                      : 'border-border bg-surface'
                  }`}
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
                    <div className="flex min-w-0 flex-1 items-start gap-3 md:items-center">
                      <span className="tabular mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-fg md:mt-0">
                        {item.sr}
                      </span>
                      <p className="min-w-0 flex-1 text-sm leading-relaxed text-foreground">
                        {item.description}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center md:shrink-0">
                      <InspectionStatusToggle
                        value={item.status}
                        disabled={readOnly}
                        name={`Item ${item.sr} status`}
                        onChange={(status) => setItem(idx, { status })}
                      />
                      <input
                        type="text"
                        value={item.remarks}
                        disabled={readOnly}
                        onChange={(e) => setItem(idx, { remarks: e.target.value })}
                        placeholder="Remarks"
                        aria-label={`Remarks for item ${item.sr}`}
                        className="min-h-11 w-full rounded-md border border-border-strong bg-surface px-3 text-base text-foreground transition-colors duration-fast hover:border-muted-fg focus:border-accent disabled:bg-muted disabled:text-muted-fg sm:w-48 sm:text-sm"
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-fg">
              Spare parts used
            </h4>
            {!readOnly && (
              <Button variant="ghost" size="sm" icon={AddOutlined} onClick={addSparePart}>
                Add part
              </Button>
            )}
          </div>

          <div className="space-y-3">
            {spareParts.map((part, idx) => (
              <div key={idx} className="rounded-lg border border-border bg-muted/40 p-3 sm:p-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Input
                    label="Device name"
                    value={part.deviceName}
                    disabled={readOnly}
                    onChange={(e) => setSparePart(idx, { deviceName: e.target.value })}
                  />
                  <Input
                    label="Description"
                    value={part.description}
                    disabled={readOnly}
                    onChange={(e) => setSparePart(idx, { description: e.target.value })}
                  />
                  <Input
                    label="Quantity"
                    inputMode="numeric"
                    value={part.quantity}
                    disabled={readOnly}
                    onChange={(e) => setSparePart(idx, { quantity: e.target.value })}
                  />
                  <Input
                    label="Remarks"
                    value={part.remarks}
                    disabled={readOnly}
                    onChange={(e) => setSparePart(idx, { remarks: e.target.value })}
                  />
                </div>
                {!readOnly && spareParts.length > 1 && (
                  <div className="mt-2 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={DeleteOutline}
                      onClick={() => removeSparePart(idx)}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-3">
          <Textarea
            label="Comments"
            className="lg:col-span-2"
            rows={3}
            value={inspection.comments}
            disabled={readOnly}
            onChange={(e) => setInspection((p) => ({ ...p, comments: e.target.value }))}
            placeholder="Notes, observations, follow-up actions…"
          />
          <div>
            <Input
              label="Prepared by"
              required
              value={inspection.preparedBy}
              disabled={readOnly}
              onChange={(e) => setInspection((p) => ({ ...p, preparedBy: e.target.value }))}
              placeholder="Technician name"
            />
            {inspection.completedAt && (
              <div className="mt-2 space-y-0.5 text-xs text-muted-fg">
                <p>Completed by: {inspection.completedByUser || 'N/A'}</p>
                <p>Completed: {new Date(inspection.completedAt).toLocaleString('en-GB')}</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default InspectionChecklistModal;
